import { useState } from 'react'
import { X, Clock, Timer, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { HabitLogData } from '@/types'

interface HabitLogModalProps {
  habitName: string
  initialData?: HabitLogData | null
  mode?: 'new' | 'edit'
  onSave: (data: HabitLogData) => void
  onSkip: () => void
  onUncomplete?: () => void
}

export function HabitLogModal({
  habitName,
  initialData,
  mode = 'new',
  onSave,
  onSkip,
  onUncomplete,
}: HabitLogModalProps) {
  const [startedAt, setStartedAt] = useState(initialData?.startedAt ?? '')
  const [durationMinutes, setDurationMinutes] = useState(
    initialData?.durationMinutes?.toString() ?? ''
  )
  const [note, setNote] = useState(initialData?.note ?? '')

  const handleSave = () => {
    onSave({
      startedAt: startedAt || null,
      durationMinutes: durationMinutes ? parseInt(durationMinutes, 10) : null,
      note: note || null,
    })
  }

  const hasData = startedAt || durationMinutes || note

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
      <div className="bg-card w-full max-w-md rounded-t-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-semibold">
            {mode === 'edit' ? 'ログを編集' : 'ログを記録'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onSkip}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-4 p-4">
          <p className="text-muted-foreground text-sm">{habitName}</p>

          {/* 開始時刻 */}
          <div className="space-y-2">
            <Label htmlFor="started-at" className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              開始時刻
            </Label>
            <Input
              id="started-at"
              type="time"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              className="touch-target"
            />
          </div>

          {/* 所要時間 */}
          <div className="space-y-2">
            <Label htmlFor="duration" className="flex items-center gap-1.5">
              <Timer className="h-4 w-4" />
              所要時間（分）
            </Label>
            <Input
              id="duration"
              type="number"
              min="1"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              placeholder="例: 30"
              className="touch-target"
            />
          </div>

          {/* メモ */}
          <div className="space-y-2">
            <Label htmlFor="note" className="flex items-center gap-1.5">
              <FileText className="h-4 w-4" />
              メモ
            </Label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="自由にメモを記録..."
              rows={3}
              className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            />
            <p className="text-muted-foreground text-right text-xs">
              {note.length}/500
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onSkip}
              className="touch-target flex-1"
            >
              {mode === 'edit' ? '閉じる' : 'スキップ'}
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!hasData}
              className="touch-target flex-1"
            >
              保存
            </Button>
          </div>

          {mode === 'edit' && onUncomplete && (
            <div className="pt-2">
              <Button
                type="button"
                variant="destructive"
                onClick={onUncomplete}
                className="touch-target w-full"
              >
                完了を取り消す
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
